import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

const AttendeeList = ({ attendees = [], loading, stats }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date"); // name, date, status
  const [sortOrder, setSortOrder] = useState("desc");

  const sortedAndFilteredAttendees = useMemo(() => {
    let filtered = attendees.filter((attendee) => {
      const firstName = (attendee.firstName || "").toLowerCase();
      const lastName = (attendee.lastName || "").toLowerCase();
      const email = (attendee.email || "").toLowerCase();
      const status = (attendee.status || "pending").toLowerCase();
      
      const matchesSearch = 
        firstName.includes(searchTerm.toLowerCase()) ||
        lastName.includes(searchTerm.toLowerCase()) ||
        email.includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || status.includes(statusFilter.toLowerCase());
      
      return matchesSearch && matchesStatus;
    });

    return filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === "name") {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (sortBy === "date") {
        comparison = new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      } else if (sortBy === "status") {
        comparison = (a.status || "pending").localeCompare(b.status || "pending");
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [attendees, searchTerm, statusFilter, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Helper for attendee avatar initials/colors
  const getAttendeeColor = (name) => {
    const colors = ["bg-primary", "bg-success", "bg-info", "bg-warning", "bg-danger"];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="attendees-section mt-4">
      {/* Attendee Statistics Cards */}
      <div className="row g-4">
        <div className="col-12 col-md-4">
          <div className="event-stat-card">
            <div className="card-top">
              <div>
                <p className="card-label">Total Attendees</p>
                <h2 className="card-value">{stats.totalAttendees}</h2>
              </div>
              <div className="card-icon-wrapper icon-bg-blue">
                <i className="isax isax-user icon-text-blue" />
              </div>
            </div>
            <div className="card-footer">
              <p className="description">Registered fans</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="event-stat-card">
            <div className="card-top">
              <div>
                <p className="card-label">Check-ins</p>
                <h2 className="card-value">{stats.checkIns}</h2>
              </div>
              <div className="card-icon-wrapper icon-bg-green">
                <i className="isax isax-user-tick icon-text-green" />
              </div>
            </div>
            <div className="card-footer">
              <p className="description">At venue today</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="event-stat-card">
            <div className="card-top">
              <div>
                <p className="card-label">Ticket Revenue</p>
                <h2 className="card-value">${stats.ticketRevenue}</h2>
              </div>
              <div className="card-icon-wrapper icon-bg-blue">
                <i className="isax isax-card icon-text-blue" />
              </div>
            </div>
            <div className="card-footer">
              <p className="description">Total ticket sales</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Table Card */}
      <div className="modern-card mt-5">
        {/* Filters Bar */}
        <div className="modern-filter-bar">
          <div className="d-flex flex-column flex-md-row gap-4">
            <div className="modern-search-wrapper w-100">
              <i className="isax isax-search-normal-1" />
              <input
                type="text"
                className="modern-search-input"
                placeholder="Search attendees by name, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="d-flex flex-wrap align-items-center gap-3">
              <div className="modern-select-wrapper">
                <i className="isax isax-filter prefix-icon" />
                <select 
                  className="modern-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="checked-in">Checked In</option>
                  <option value="pending">Pending</option>
                </select>
                <i className="isax isax-arrow-down-1 suffix-icon" />
              </div>
              <div className="results-badge">
                {loading ? <span>Loading...</span> : (
                  <>
                    <span className="fw-bold text-dark me-1">{sortedAndFilteredAttendees.length}</span>
                    <span>attendees</span>
                  </>
                )}
              </div>
              <button className="btn btn-outline-primary btn-sm rounded-pill px-4">
                <i className="isax isax-export me-1"></i> Export List
              </button>
            </div>
          </div>
        </div>

        {/* Table Header */}
        <div className="modern-table-header flex-column flex-md-row align-items-start align-items-md-center">
          <h2 className="fw-bold mb-3 mb-md-0">Attendee Management ({sortedAndFilteredAttendees.length})</h2>
          <div className="d-flex flex-wrap align-items-center gap-3 gap-md-4">
            <span className="text-muted small">Sort by:</span>
            <div className="d-flex gap-2">
              <button 
                className={`sort-btn ${sortBy === 'name' ? 'active' : ''}`}
                onClick={() => handleSort('name')}
              >
                Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button 
                className={`sort-btn ${sortBy === 'date' ? 'active' : ''}`}
                onClick={() => handleSort('date')}
              >
                Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button 
                className={`sort-btn ${sortBy === 'status' ? 'active' : ''}`}
                onClick={() => handleSort('status')}
              >
                Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-5 text-center">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : sortedAndFilteredAttendees.length > 0 ? (
          <div className="table-responsive">
            <table className="table modern-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Attendee Details</th>
                  <th>Event</th>
                  <th className="text-center">Status</th>
                  <th>Reg. Date</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedAndFilteredAttendees.map((attendee) => (
                  <tr key={attendee._id || attendee.id}>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <div className={`event-avatar ${getAttendeeColor(attendee.firstName || "U")} text-white`}>
                          {(attendee.firstName || "U")[0]}{(attendee.lastName || "")[0]}
                        </div>
                        <div>
                          <p className="fw-bold text-dark mb-0">{attendee.firstName} {attendee.lastName}</p>
                          <p className="text-muted small mb-0">{attendee.email}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="fw-medium text-dark small">
                        {attendee.eventId?.title || "Unknown Event"}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`status-badge ${(attendee.status || "pending").replace("-", "").toLowerCase()}`}>
                        {attendee.status}
                      </span>
                    </td>
                    <td>
                      <span className="text-muted small">
                        {attendee.createdAt ? new Date(attendee.createdAt).toLocaleDateString() : "N/A"}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex justify-content-end gap-1">
                        <button className="modern-action-btn view" title="View Details">
                          <i className="isax isax-eye"></i>
                        </button>
                        <button className="modern-action-btn edit" title="Resend Ticket">
                          <i className="isax isax-send-2"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-5 text-center bg-light bg-opacity-25">
             <div className="mx-auto bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-4" style={{ width: "80px", height: "80px" }}>
               <i className="isax isax-people text-primary" style={{ fontSize: "40px" }}></i>
             </div>
             <h3 className="fw-bold text-dark mb-2">No Attendees Found</h3>
             <p className="text-muted mb-0">No one has registered for your events yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendeeList;
